import { CommentAltIcon, DownloadIcon, EmailIcon, EmailResendIcon, EyeIcon } from "@xola/icons";
import { Badge, Button, flash, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ReactNode, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    Asset,
    CommunicationChannelType,
    DeliveryReport,
    Message,
    MessageFormatType,
    MessageType,
    Participant,
    Purchase,
    PurchaseItem,
} from "types";
import { removeNonBreakingSpaces } from "../../../../helpers/validationHelper";
import { useSeller, useUser } from "../../../../hooks/useAccount";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { useInstalledPlugins } from "../../../../hooks/usePlugins";
import { NoTranslate } from "../../../Common/NoTranslate";
import { MessagePurchaseModal } from "../../../PurchaseModification/MessagePurchaseModal";
import { TimelineItem } from "../TimelineItem";
import { EmailPreviewDrawer } from "./EmailPreviewDrawer";
import { SmsDeliveryStatus, SmsRecipients } from "./SmsDelivery";
import { api } from "@/api/api";
import { xolaUri } from "@/api/xolaUri";
import { MessageSendTo } from "@/components/Common/MessageModal/messageDrawerHelpers";
import { RecipientOverflowList } from "@/components/PurchasesMenu/Timeline/Items/RecipientOverflowList";
import { richTextClassName } from "@/helpers/richTextHelper";
import { useModal } from "@/hooks/useVisibility";
import { getDeliveryRecipients, getUnreportedRecipients } from "@/models/DeliveryReport";
import { getInboundSenderName, isSent, isSmsMessage } from "@/models/Message";

interface MessageTimelineItemProps {
    showDate?: boolean;
    message: Message;
    purchase: Purchase;
    item: PurchaseItem;
    deliveryReports?: DeliveryReport[];
}

const RecipientList = ({ recipients }: { recipients: Participant[] }) => {
    if (recipients.length === 0) {
        return null;
    }

    const entries = recipients.map(({ id, name }) => ({ key: id, label: name }));

    return (
        <>
            To:&nbsp;
            <NoTranslate>
                (<RecipientOverflowList recipients={entries} />)
            </NoTranslate>
        </>
    );
};

const getAssetUrl = (asset: Asset) => {
    if (!asset?.path) {
        return null;
    }
    return asset.path.startsWith("http") ? asset.path : `${xolaUri}${asset.path}`;
};

// Only the fields the derivation actually reads, so the memo below can depend on exactly those.
type MessageParts = Pick<Message, "formats" | "channels" | "type" | "deletedAt">;

const deriveMessageParts = (message: MessageParts) => {
    const formats = message.formats ?? [];
    const channels = message.channels ?? [];
    const isLegacy = formats.length === 0;
    const isDeleted = !!message.deletedAt;
    const hasMedia = formats.some((format) => format.type === MessageFormatType.MEDIA);
    const appChannelId = channels.find(
        (channel) => channel !== CommunicationChannelType.EMAIL && channel !== CommunicationChannelType.SMS,
    );
    return {
        emailFormat: formats.find((format) => format.type === MessageFormatType.EMAIL),
        textFormat: formats.find((format) => format.type === MessageFormatType.TEXT),
        mediaFormat: formats.find((format) => format.type === MessageFormatType.MEDIA),
        appChannelId: appChannelId ?? null,
        isDeleted,
        showRichTextItem:
            formats.some((format) => format.type === MessageFormatType.EMAIL) ||
            (isLegacy && message.type === MessageType.EMAIL),
        showSmsItem: isSmsMessage(message),
        // App-channel messages can carry only media (e.g. an attachment with no text body) or be deleted with an
        // empty body. Still surface the item in those cases so the image / deletion notice is shown.
        showAppChannelItem:
            !!appChannelId &&
            (formats.some((format) => format.type === MessageFormatType.TEXT) || hasMedia || isDeleted),
    };
};

const getRespondRecipient = (item: PurchaseItem, from?: string): MessageSendTo[] | undefined => {
    if (!from) {
        return undefined;
    }

    const isSenderOnReservation =
        from === item.organizer?.user?.id || item.guests?.some((guest) => guest?.traveler?.id === from);

    return isSenderOnReservation ? [{ id: from, type: "traveler" }] : undefined;
};

export const MessageTimelineItem = ({
    purchase,
    item,
    message,
    deliveryReports = [],
    showDate = true,
}: MessageTimelineItemProps) => {
    const user = useUser();
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const previewModal = useModal(false);
    const { data: installedPlugins = [] } = useInstalledPlugins();

    const { mutateAsync: resendEmail, isLoading: isResending } = useMutation(async (emailId: ID) =>
        api.post(`/emails/${emailId}/resend`),
    );

    const isSentMessage = isSent({ message, seller, user });
    const action = isSentMessage ? "Sent" : "Received";

    const { formats, channels, type, deletedAt } = message;
    const {
        emailFormat,
        textFormat,
        mediaFormat,
        appChannelId,
        isDeleted,
        showRichTextItem,
        showSmsItem,
        showAppChannelItem,
    } = useMemo(() => deriveMessageParts({ formats, channels, type, deletedAt }), [formats, channels, type, deletedAt]);

    const hasDeliveryReports = deliveryReports.length > 0;
    const deliveryRecipients = getDeliveryRecipients(item, deliveryReports, purchase);
    const unreportedRecipients = getUnreportedRecipients(item, message.to ?? [], deliveryReports, purchase);

    const appChannelPlugin = appChannelId ? installedPlugins.find((p) => p.id === appChannelId) : undefined;
    const appChannelName = appChannelPlugin?.name ?? "Airbnb";

    const richTextTitle = `Email ${action}`;
    const smsTitle = `Text Message ${action}`;
    const appChannelTitle = `${appChannelName} Message ${action}`;
    const textBody = textFormat?.body;
    const inboundSenderName = getInboundSenderName(message, item.guests);

    const respondTo = getRespondRecipient(item, message.from);
    const respondDescription = respondTo
        ? "This message will be sent to the traveler"
        : "This message will only be sent to the organizer of this reservation.";

    const handleMessageModal = () => {
        setContactModalOpen(!contactModalOpen);
        invalidate("conversations", purchase.conversation.id);
    };

    const handleResend = async () => {
        if (!emailFormat?.email?.id || isResending) {
            return;
        }

        try {
            await resendEmail(emailFormat.email.id);
            invalidate("conversations", purchase.conversation.id);
            flash.success("Email resent");
        } catch {
            flash.danger("Failed to resend email. Please try again.");
        }
    };

    const renderBody = (bodyHtml: string) => (
        <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: removeNonBreakingSpaces(bodyHtml) }}
            className={clsx(
                "no-translate inline-block max-w-full self-start rounded border border-gray-light bg-gray-light p-3",
                richTextClassName,
                isSentMessage ? null : "bg-blue-lighter",
            )}
            data-testid="message-timeline-item-message-body-field"
        />
    );

    const renderDeletedNotice = () => (
        <div
            className={clsx(
                "inline-block max-w-full self-start rounded border border-gray-light bg-gray-light p-3 italic text-gray-darker",
                isSentMessage ? null : "bg-blue-lighter",
            )}
            data-testid="message-timeline-item-deleted-field"
        >
            Message deleted
        </div>
    );

    const renderRecipientBadge = (recipients: ReactNode) => (
        <Badge
            color="secondary"
            data-testid="message-timeline-item-sent-to-field"
            className="place-self-start self-start"
        >
            {recipients}
        </Badge>
    );

    const renderRecipients = () =>
        message.to && message.to.length > 0 ? renderRecipientBadge(<RecipientList recipients={message.to} />) : null;

    const renderRespondButton = () =>
        isSentMessage ? null : (
            <div>
                <Button size="small" icon={<CommentAltIcon />} onClick={handleMessageModal}>
                    Respond
                </Button>
            </div>
        );

    const renderEmailActions = () =>
        emailFormat?.email?.id ? (
            <div className="flex flex-row space-x-2">
                {isSentMessage && (
                    <SubmitButton
                        size="small"
                        variant="outline"
                        color="secondary"
                        icon={<EmailResendIcon />}
                        isLoading={isResending}
                        onClick={handleResend}
                    >
                        Resend
                    </SubmitButton>
                )}
                <Button size="small" variant="outline" color="secondary" icon={<EyeIcon />} onClick={previewModal.show}>
                    Preview
                </Button>
            </div>
        ) : null;

    return (
        <>
            {showRichTextItem && (
                <TimelineItem
                    title={richTextTitle}
                    message={message}
                    icon={<EmailIcon />}
                    color="primary"
                    showDate={showDate}
                    purchase={purchase}
                >
                    <div className="flex flex-col space-y-1.5">
                        <div className="flex items-center justify-between">
                            {renderRecipients()}
                            {renderEmailActions()}
                        </div>
                        {isDeleted ? (
                            renderDeletedNotice()
                        ) : (
                            <>
                                {emailFormat?.body && renderBody(emailFormat.body)}
                                {mediaFormat?.asset && <MediaAttachment asset={mediaFormat.asset} />}
                            </>
                        )}
                        {renderRespondButton()}
                    </div>
                </TimelineItem>
            )}

            {showSmsItem && (
                <TimelineItem
                    title={smsTitle}
                    message={message}
                    icon={<CommentAltIcon />}
                    color="primary"
                    showDate={showDate}
                    purchase={purchase}
                    senderName={inboundSenderName}
                >
                    <div className="flex flex-col space-y-1.5">
                        {hasDeliveryReports ? (
                            <div className="flex items-center justify-between">
                                {renderRecipientBadge(
                                    <SmsRecipients
                                        recipients={deliveryRecipients}
                                        unreportedRecipients={unreportedRecipients}
                                    />,
                                )}
                                <SmsDeliveryStatus recipients={deliveryRecipients} />
                            </div>
                        ) : (
                            renderRecipients()
                        )}
                        {isDeleted ? renderDeletedNotice() : textBody && renderBody(textBody)}
                        {renderRespondButton()}
                    </div>
                </TimelineItem>
            )}

            {showAppChannelItem && (
                <TimelineItem
                    title={appChannelTitle}
                    message={message}
                    icon={<CommentAltIcon />}
                    color="primary"
                    showDate={showDate}
                    purchase={purchase}
                >
                    <div className="flex flex-col space-y-1.5">
                        {renderRecipients()}
                        {isDeleted ? (
                            renderDeletedNotice()
                        ) : (
                            <>
                                {textBody && renderBody(textBody)}
                                {mediaFormat?.asset && <MediaAttachment asset={mediaFormat.asset} />}
                            </>
                        )}
                        {renderRespondButton()}
                    </div>
                </TimelineItem>
            )}

            <MessagePurchaseModal
                isOrganizerOnly
                purchase={purchase}
                item={item}
                to={respondTo}
                preferredChannels={channels}
                description={respondDescription}
                isOpen={contactModalOpen}
                onClose={handleMessageModal}
                onContinue={handleMessageModal}
            />

            {emailFormat?.email?.id && (
                <EmailPreviewDrawer
                    emailId={emailFormat.email.id}
                    isOpen={previewModal.isVisible}
                    onClose={previewModal.hide}
                />
            )}
        </>
    );
};

const MediaAttachment = ({ asset }: { asset: Asset }) => {
    const url = getAssetUrl(asset);

    if (!url) {
        return null;
    }

    if (asset.type?.startsWith("image/")) {
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="inline-block max-w-xs">
                <img src={url} alt={asset.name ?? "attachment"} className="max-w-md rounded border border-gray-light" />
            </a>
        );
    }

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 self-start rounded border border-gray-light bg-white px-4 py-2 text-sm font-bold text-black hover:border-gray"
        >
            <DownloadIcon />
            <span>Download PDF</span>
        </a>
    );
};
